import React, { useRef, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import Typewriter from '@components/typewriter';
import {
  RenderTypeEnum,
  consoleState,
  AppendOutputType,
  TypingOutputType,
  TextLinkOutputType,
  ViewTypeEnum,
  OpenLinkTarget,
} from '@internal/console/atom';
import { viewTypeSelector, Size } from '@internal/window/selectors'


import './index.scss'

type TerminalOutputProps = {}

type OpenConsoleLinkHandlers = {
  open?: (url: string, target: OpenLinkTarget) => void;
  assign?: (url: string) => void;
};

export function openConsoleLink(
  url: string,
  target: OpenLinkTarget = "_blank",
  handlers: OpenConsoleLinkHandlers = {},
): void {
  const open = handlers.open ?? ((nextUrl: string, nextTarget: OpenLinkTarget) => {
    window.open(nextUrl, nextTarget, "noreferrer");
  });
  const assign = handlers.assign ?? ((nextUrl: string) => {
    window.location.assign(nextUrl);
  });

  if (target === "_self") {
    assign(url);
    return;
  }

  open(url, target);
}

const TerminalOutput = ({ }: TerminalOutputProps): JSX.Element => {
  const dummyRef = useRef(null);

  const [
    {
      rendering,
      renderDelay: delay,
      appendDelay,
      view,
      input,
    },
    setConsoleState,
  ] = useRecoilState(consoleState)
  const viewType = useRecoilValue(viewTypeSelector);
  const renderingText = rendering[0];

  function hasNextLine(): boolean {
    return rendering.length > 0;
  }

  useEffect(() => {
    if (hasNextLine() && renderingText.type === RenderTypeEnum.Append) {
      const timeout = setTimeout(() => {
        setConsoleState((prevState) => ({
          rendering: prevState.rendering.slice(1),
          renderDelay: prevState.renderDelay,
          appendDelay: prevState.appendDelay,
          view: [...prevState.view, {
            type: ViewTypeEnum.Text,
            text: (prevState.rendering[0] as AppendOutputType).text,
          }],
          input,
        }))

        dummyRef.current.scrollIntoView({
          behavior: "smooth",
        });
      }, appendDelay);

      return () => clearTimeout(timeout)
    }

    if (hasNextLine() && renderingText.type === RenderTypeEnum.OpenLink) {
      const timeout = setTimeout(() => {
        setConsoleState((prevState) => ({
          rendering: prevState.rendering.slice(1),
          renderDelay: prevState.renderDelay,
          appendDelay: prevState.appendDelay,
          view: prevState.view,
          input,
        }))

        openConsoleLink(renderingText.url, renderingText.target);
      }, appendDelay);

      return () => clearTimeout(timeout)
    }

    if (hasNextLine() && renderingText.type === RenderTypeEnum.TextLink) {
      const timeout = setTimeout(() => {
        setConsoleState((prevState) => ({
          rendering: prevState.rendering.slice(1),
          renderDelay: prevState.renderDelay,
          appendDelay: prevState.appendDelay,
          view: [...prevState.view, {
            type: ViewTypeEnum.Link,
            text: (prevState.rendering[0] as TextLinkOutputType).text,
            url: (prevState.rendering[0] as TextLinkOutputType).url,
          }],
          input,
        }))
      }, appendDelay);

      return () => clearTimeout(timeout)
    }
  }, [renderingText])

  function onTypeChange() {
    dummyRef.current.scrollIntoView({
      behavior: "smooth",
    });
  }

  function addToView(): void {
    if (rendering.length <= 0) {
      return;
    }

    setConsoleState((prevState) => ({
      rendering: prevState.rendering.slice(1),
      renderDelay: prevState.renderDelay,
      appendDelay: prevState.appendDelay,
      view: [...prevState.view, {
        type: ViewTypeEnum.Text,
        text: (prevState.rendering[0] as TypingOutputType).text,
      }],
      input,
    }))
  }

  function handleDoneTyping(reset: () => void) {
    addToView();
    reset();
  }

  const additionalClasses = viewType.size === Size.Small
    ? " output-small-view"
    : "";

  return (
    <div className={"terminal-output-container" + additionalClasses}>
      <div className="console-spacer"></div>
      {view.map((line, index) => {
        if (line.type === ViewTypeEnum.Text) {
          return <div key={index} className="console-text"><pre>{line.text}</pre></div>
        }

        if (line.type === ViewTypeEnum.Link) {
          return (
            <div key={index} className="console-text"><pre>
              <a href={line.url} target="_blank" rel="noreferrer">{line.text}</a>
            </pre></div>
          )
        }
        })}
      {hasNextLine() && renderingText.type === RenderTypeEnum.Typing
        ? <div className="console-text"><pre>
            <Typewriter
              text={renderingText.text}
              delay={delay}
              onChange={onTypeChange}
              done={handleDoneTyping} />
          </pre></div>
        : null}
      <div ref={dummyRef} />
    </div>
  );
};

export default TerminalOutput;
